import { OptionsMenuStorybook, TooltipPrimeReact } from "@finanedi.es/storybook"
import {
  faBadgeCheck,
  faPen,
  faStar as faStarLight,
  faTrashCan,
  faXmarkCircle,
} from "@fortawesome/pro-light-svg-icons"
import { faStar as faStarSolid } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { CuentaBancariaContactoReadingDtoProps } from "api/DTOs/CuentaBancariaContactoReadingDto"
import { ESTADO_CUENTA_TITULARIDAD } from "api/Types/AccountStatus"
import { TIPO_REMESA } from "api/Types/TipoRemesa"
import { DEFAULT_BANK_LOGO_URL } from "features/Tesoreria/pages/Bancos/constants"
import { Skeleton } from "primereact/skeleton"
import { Dispatch, SetStateAction } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { frontendDateFormat } from "utils/formatDate"
import { reverseBlockSplit } from "utils/reverseBlockSplit"
import { CreateEditEntidadModalProps, DeleteEntidadModalProps } from "../.."
import { useMarkPrincipalCuentaBancariaContactos } from "./UseMarkPrincipalCuentaBancaria"

export interface CuentaBancariaContactoWithLogoProps
  extends CuentaBancariaContactoReadingDtoProps {
  logo?: string | null
}

interface CardBankAcountContactProps {
  bankAccount: CuentaBancariaContactoWithLogoProps
  setShowDeleteModal: Dispatch<SetStateAction<DeleteEntidadModalProps>>
  setCreateOrEditModal: Dispatch<SetStateAction<CreateEditEntidadModalProps>>
  contactosCuentaBancariaByIdRefetch: () => void
}

const CardBankAcountContact = ({
  bankAccount,
  setShowDeleteModal,
  setCreateOrEditModal,
  contactosCuentaBancariaByIdRefetch,
}: CardBankAcountContactProps) => {
  const { t } = useTranslation()
  const { id = "" } = useParams<{ id: string }>()
  const { markPrincipal } = useMarkPrincipalCuentaBancariaContactos(
    id,
    contactosCuentaBancariaByIdRefetch
  )

  return (
    <div className='tw-w-full tw-h-auto tw-flex tw-flex-col tw-p-6 tw-gap-6 tw-rounded-lg tw-shadow-md tw-border tw-border-neutral-25'>
      <div className='tw-flex tw-flex-row tw-justify-between tw-items-center tw-gap-2'>
        <div className='tw-flex tw-flex-col tw-flex-1 tw-min-w-0 tw-mr-4'>
          <div className='tw-flex tw-flex-row tw-items-center tw-gap-0.5 tw-flex-1 tw-min-w-0'>
            <span className='tw-w-fit tw-truncate tw-whitespace-nowrap tw-text-start tw-min-w-0 tw-text-sm tw-font-medium'>
              {bankAccount.entidadBancariaNombre}
            </span>
            <FontAwesomeIcon
              icon={faStarSolid}
              data-is-principal={bankAccount.principal}
              className='tw-h-3.5 tw-w-3.5 tw-flex-shrink-0 data-[is-principal=false]:tw-hidden !tw-text-yellow-300'
            />
          </div>

          <div className='tw-flex tw-flex-row tw-items-center tw-gap-0.5'>
            <span className='tw-text-text-hyperlinks tw-text-sm tw-items-center tw-h-fit'>
              {bankAccount.iban ? (
                reverseBlockSplit(bankAccount.iban, 4)
              ) : (
                <Skeleton className='!tw-h-5 !tw-w-52' />
              )}
            </span>
            <TooltipPrimeReact
              target={`.ownership-${bankAccount.id}`}
              content={
                bankAccount.estadoVerificacionTitularidad ===
                ESTADO_CUENTA_TITULARIDAD.CONFIRMED
                  ? t("contactos.cuentasBancarias.ownerShipVerified")
                  : t("contactos.cuentasBancarias.ownerNotShipVerified")
              }
            />
            <FontAwesomeIcon
              icon={
                bankAccount.estadoVerificacionTitularidad ===
                ESTADO_CUENTA_TITULARIDAD.CONFIRMED
                  ? faBadgeCheck
                  : faXmarkCircle
              }
              data-is-verified={
                bankAccount.estadoVerificacionTitularidad ===
                  ESTADO_CUENTA_TITULARIDAD.CONFIRMED ||
                bankAccount.estadoVerificacionTitularidad ===
                  ESTADO_CUENTA_TITULARIDAD.REJECTED
              }
              className={`tw-h-3.5 tw-w-3.5 ${
                bankAccount.estadoVerificacionTitularidad ===
                ESTADO_CUENTA_TITULARIDAD.CONFIRMED
                  ? "tw-text-success"
                  : "tw-text-danger"
              } tw-m-0.5 ownership-${
                bankAccount.id
              } data-[is-verified=false]:tw-hidden`}
            />
          </div>
        </div>

        <OptionsMenuStorybook
          id={bankAccount.id}
          items={[
            {
              title: `${t("contactos.cuentasBancarias.edit")}`,
              hasRedirect: true,
              action: () =>
                setCreateOrEditModal({
                  show: true,
                  isEdit: bankAccount,
                }),
              icon: faPen,
            },
            {
              title: `${t("contactos.cuentasBancarias.principal")}`,
              hasRedirect: true,
              show: !bankAccount.principal,
              action: () => {
                markPrincipal(bankAccount)
              },
              icon: faStarLight,
            },
            {
              title: `${t("contactos.cuentasBancarias.delete")}`,
              hasRedirect: true,
              action: () =>
                bankAccount.id &&
                setShowDeleteModal({
                  show: true,
                  id: bankAccount.id,
                }),
              className: "tw-text-danger",
              icon: faTrashCan,
            },
          ]}
          position='right'
        />
      </div>
      <div className='tw-w-full tw-h-36 tw-py-0.5 tw-flex tw-items-center tw-justify-center'>
        <img
          src={bankAccount?.logo || DEFAULT_BANK_LOGO_URL}
          alt={`${t("contactos.cuentasBancarias.labelImg")}`}
        />
      </div>
      <div className='tw-grid tw-grid-cols-10 tw-gap-3 tw-text-sm tw-text-start'>
        <div className='tw-col-span-4 tw-flex tw-flex-col'>
          <span className='tw-text-neutral-75'>
            {t("contactos.cuentasBancarias.refMandatoLabel")}
          </span>

          <span className='tw-w-full tw-truncate tw-text-ellipsis'>
            {bankAccount?.mandato?.referencia ?? "-"}
          </span>
        </div>
        <div className='tw-col-span-4 tw-flex tw-flex-col'>
          <span className='tw-text-neutral-75'>
            {t("contactos.cuentasBancarias.dateMandatoLabel")}
          </span>
          <span className='tw-w-full tw-truncate tw-text-ellipsis'>
            {bankAccount?.mandato?.fecha
              ? frontendDateFormat(new Date(bankAccount?.mandato?.fecha))
              : "-"}
          </span>
        </div>
        <div className='tw-col-span-2 tw-flex tw-flex-col'>
          <span className='tw-text-neutral-75'>
            {t("contactos.cuentasBancarias.typeLabel")}
          </span>
          <span>
            {bankAccount?.mandato && bankAccount?.mandato?.tipoRemesa !== null
              ? bankAccount?.mandato?.tipoRemesa === TIPO_REMESA.B2B
                ? t("contactos.cuentasBancarias.b2b")
                : t("contactos.cuentasBancarias.core")
              : "-"}
          </span>
        </div>
      </div>
    </div>
  )
}

export default CardBankAcountContact
